export type TimeSeriesPeriod = "years" | "months" | "weeks" | "days" | "hours";

export interface TimeSeriesBucket {
  /** UTC date at the start of the period this bucket represents. */
  date: Date;
  count: number;
}

export const timestampsToDates = (timestamps: string[]): Date[] => timestamps.map((ts) => new Date(ts));

/** Return a new Date normalized to the start of its period (UTC). */
export const periodStart = (date: Date, period: TimeSeriesPeriod): Date => {
  const d = new Date(date.getTime());
  if (period === "hours") {
    d.setUTCMinutes(0, 0, 0);
    return d;
  }
  if (period === "days") {
    d.setUTCHours(0, 0, 0, 0);
    return d;
  }
  if (period === "months") {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
  }
  if (period === "years") {
    return new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  }
  // weeks — snap to ISO Monday (UTC)
  const day = d.getUTCDay() || 7; // Sun=0 -> 7
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  monday.setUTCDate(monday.getUTCDate() - (day - 1));
  return monday;
};

/** Return a new Date offset by `n` periods from `date`. */
export const stepPeriod = (date: Date, period: TimeSeriesPeriod, n: number): Date => {
  const d = new Date(date.getTime());
  if (period === "hours") {
    d.setUTCHours(d.getUTCHours() + n);
  } else if (period === "days") {
    d.setUTCDate(d.getUTCDate() + n);
  } else if (period === "weeks") {
    d.setUTCDate(d.getUTCDate() + n * 7);
  } else if (period === "months") {
    d.setUTCMonth(d.getUTCMonth() + n);
  } else {
    d.setUTCFullYear(d.getUTCFullYear() + n);
  }
  return d;
};

/** Number of periods to display, based on the first datetime and now. */
export const calculateNumberOfPeriods = (sortedDatetimes: Date[], period: TimeSeriesPeriod): number => {
  if (!sortedDatetimes || sortedDatetimes.length === 0) return 0;
  const extra = 2;
  const start = periodStart(sortedDatetimes[0], period);
  const end = periodStart(new Date(), period);

  const MS_PER_HOUR = 1000 * 60 * 60;
  const MS_PER_DAY = MS_PER_HOUR * 24;
  const diffMs = end.getTime() - start.getTime();

  if (period === "hours") return Math.ceil(diffMs / MS_PER_HOUR) + extra;
  if (period === "days") return Math.ceil(diffMs / MS_PER_DAY) + extra;
  if (period === "weeks") return Math.ceil(diffMs / (MS_PER_DAY * 7)) + extra;
  if (period === "months") {
    const months = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth());
    return months + 1 + extra;
  }
  // years
  return end.getUTCFullYear() - start.getUTCFullYear() + 1 + extra;
};

/**
 * Bucket the given sorted datetimes into fixed-length periods, filling gaps with zero
 * counts. Returns the last `numPeriods` buckets ending at "now".
 */
export const buildTimeSeriesBuckets = (
  sortedDatetimes: Date[],
  period: TimeSeriesPeriod,
  numPeriods: number
): TimeSeriesBucket[] => {
  if (numPeriods <= 0) return [];

  const counts = new Map<number, number>();
  for (const dt of sortedDatetimes) {
    const key = periodStart(dt, period).getTime();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const anchor = periodStart(new Date(), period);
  const buckets: TimeSeriesBucket[] = [];
  for (let i = numPeriods - 1; i >= 0; i -= 1) {
    const date = stepPeriod(anchor, period, -i);
    buckets.push({ date, count: counts.get(date.getTime()) ?? 0 });
  }
  return buckets;
};

/** ISO week number for a UTC-normalized Monday date. */
export const getIsoWeekNumber = (mondayUtc: Date): number => {
  const d = new Date(mondayUtc.getTime());
  // Thursday of the same ISO week determines the ISO year/week
  d.setUTCDate(d.getUTCDate() + 3);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
};
